/**
 * `lib/skin` — the selectable skin: the registry, the persisted choice, and the one
 * attribute that applies it.
 *
 * A skin is a repaint and nothing else. `styles/skins.css` restates the §3.1 raw
 * palette under `:root[data-skin='…']`, `tokens.css` §3.2 routes every component
 * through a semantic alias, and so writing one attribute on `<html>` repaints all
 * eleven screens with zero component involvement. This module owns that attribute,
 * the list of legal values, and where the choice is kept.
 *
 * ## Why this file mirrors `lib/perf.ts` so closely
 *
 * The low-ink toggle solved the same problem first: a user preference that must be
 * applied to the document before anything renders, that lives in the shared
 * `bl_prefs_v1` blob, and that has to survive a `localStorage` accessor which is
 * allowed to *throw* rather than return null (§13.7). Every access here is wrapped the
 * way `store/persist` wraps its own, and reading `globalThis.localStorage` is inside
 * the `try` because the property access is itself the throwing operation in Safari's
 * blocked-cookies mode.
 *
 * `store/persist` never imports this module, so there is no cycle.
 *
 * ## Two tiers, and why the cloud one is worth the round trip
 *
 * `UiPrefs` is documented as per-device by nature — the last tab and the low-ink flag
 * genuinely are. A skin is not: a person who picks ЭФИР has picked it for *their
 * player*, and finding the shipped orange again on their tablet reads as the choice
 * having been lost. So the choice is written twice:
 *
 *  - **`localStorage`, synchronously**, into the `skin` field of `bl_prefs_v1`. This is
 *    the tier that matters for the next boot: the inline script in `index.html` reads
 *    it before the bundle has parsed, so the first painted frame is already the right
 *    colour and there is no flash of the previous skin.
 *  - **Telegram `CloudStorage`, best effort**, under {@link SKIN_CLOUD_KEY}. One key,
 *    one short value, far inside the 4096-character cap. It is read once at boot and
 *    only *after* the local value has already been applied, so a slow or missing cloud
 *    tier costs nothing visible. When it disagrees with the local mirror the cloud
 *    wins, because it is the tier that knows about the user's other devices.
 *
 * Neither write is allowed to fail loudly. A skin that could not be saved still
 * applied for this session, and that is the part the user can see.
 */

import type { LocalStorageLike, UiPrefs } from '../store/persist';
import { DEFAULT_PREFS, PREFS_KEY, parsePrefs } from '../store/persist';

import type { CloudStorageAdapter } from './telegram';
import { telegram } from './telegram';

/* ══ the registry ══════════════════════════════════════════════════════════════════ */

/**
 * A skin's id. These strings are the `data-skin` values in `styles/skins.css` and the
 * values persisted in `bl_prefs_v1`, so renaming one silently resets everybody who had
 * chosen it. Add, don't rename.
 */
export type SkinId = 'shellac' | 'midnight' | 'valve' | 'petrol';

export interface SkinDefinition {
  readonly id: SkinId;
  /** The name on the card. Russian, caps, like every other screen title (§4.3). */
  readonly name: string;
  /**
   * The English print code under the name. English is licensed here because it is
   * quoting real label print, which is the only place §11 allows it.
   */
  readonly stamp: string;
  /** One plain Russian sentence. Sentence case — tracked Cyrillic caps shout (§12.16). */
  readonly blurb: string;
  /** The page ground, for the card's own swatch. Kept in sync with `skins.css`. */
  readonly ground: string;
  /** The ink family's fill, for the card's own swatch. */
  readonly ink: string;
  /** The primary text colour, for the card's own swatch. */
  readonly text: string;
}

/**
 * Every skin, in the order the picker lists them. The shipped look is first and is the
 * default; a skin absent from this array cannot be chosen even if `skins.css` still
 * declares it, which is deliberate — this array is the product surface, that file is
 * the paint.
 */
export const SKINS: readonly SkinDefinition[] = Object.freeze([
  Object.freeze({
    id: 'shellac' as const,
    name: 'ШЕЛЛАК',
    stamp: 'SHELLAC 45',
    blurb: 'Тёплый почти-чёрный и оранжево-красная краска. Так плеер выглядел с самого начала.',
    ground: '#0b0a09',
    ink: '#f04e23',
    text: '#efe8da',
  }),
  Object.freeze({
    id: 'midnight' as const,
    name: 'ПОЛНОЧЬ',
    stamp: 'NAVY 072',
    blurb: 'Плотный синий картон, кремовая печать и красная метка только на том, что играет.',
    ground: '#0d1c37',
    ink: '#f2542c',
    text: '#f4ecd8',
  }),
  Object.freeze({
    id: 'valve' as const,
    name: 'ЭФИР',
    stamp: 'VALVE 12AX7',
    blurb: 'Панель лампового усилителя ночью: янтарная шкала и синий глазок. Красного нет нигде.',
    ground: '#151310',
    ink: '#d89722',
    text: '#e4e1da',
  }),
  Object.freeze({
    id: 'petrol' as const,
    name: 'ПЕТРОЛЬ',
    stamp: 'PETROL 315',
    blurb: 'Тёмная сине-бирюзовая эмаль, латунная краска и жёлтая бумажная карточка поверх.',
    ground: '#06212d',
    ink: '#f67e0e',
    text: '#e1ecef',
  }),
]);

/** What a first run gets, and what an unrecognised stored value falls back to. */
export const DEFAULT_SKIN: SkinId = 'shellac';

/** The attribute `styles/skins.css` selects on. Also written by `index.html`'s pre-boot script. */
export const SKIN_ATTRIBUTE = 'data-skin';

/** `CloudStorage` key. Charset-legal for Telegram: `[A-Za-z0-9_-]{1,128}` (§13.7). */
export const SKIN_CLOUD_KEY = 'skin_v1';

/** `?skin=valve` — the escape hatch, matching `perf.ts`'s `?ink=`. */
export const SKIN_QUERY_PARAM = 'skin';

export function isSkinId(value: unknown): value is SkinId {
  return typeof value === 'string' && SKINS.some((skin) => skin.id === value);
}

export function skinById(id: SkinId): SkinDefinition {
  const found = SKINS.find((skin) => skin.id === id);
  /* `SkinId` is a union over this frozen array, so this branch exists for the type. */
  if (found === undefined) throw new Error(`skin: unknown id ${id}`);
  return found;
}

/* ══ storage ═══════════════════════════════════════════════════════════════════════ */

/** §13.7: reading `globalThis.localStorage` is itself the operation that can throw. */
function resolveStorage(explicit: LocalStorageLike | null | undefined): LocalStorageLike | null {
  if (explicit !== undefined) return explicit;
  try {
    const storage: LocalStorageLike | undefined = globalThis.localStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

function readPrefs(storage: LocalStorageLike): UiPrefs {
  try {
    return parsePrefs(storage.getItem(PREFS_KEY));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

/**
 * The persisted choice, or `null` when the user has never made one — which is not the
 * same as having chosen the default, and the picker shows it as such.
 */
export function readSkin(storage?: LocalStorageLike | null): SkinId | null {
  const store = resolveStorage(storage);
  if (store === null) return null;
  const stored = readPrefs(store).skin;
  return isSkinId(stored) ? stored : null;
}

/**
 * Persists the choice into the shared `bl_prefs_v1` blob. `null` clears it, so the next
 * boot is back on the default rather than pinned to something the user un-chose.
 *
 * A write failure (quota, private mode, a host app that wiped the origin) is swallowed:
 * §13.7 treats `localStorage` as a cache, and the caller has already applied the skin
 * for this session with {@link applySkin}.
 */
export function writeSkin(value: SkinId | null, storage?: LocalStorageLike | null): void {
  const store = resolveStorage(storage);
  if (store === null) return;
  const next: UiPrefs = { ...readPrefs(store), skin: value };
  try {
    store.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {
    /* A cache that cannot be written is not a reason to fail the choice (§13.7). */
  }
}

/**
 * Mirrors the choice to Telegram's `CloudStorage` so it follows the account rather than
 * the device. Best effort by construction: the promise is never awaited by the caller
 * and every failure resolves to `false`.
 */
export async function pushSkinToCloud(
  value: SkinId | null,
  cloud: CloudStorageAdapter = telegram.cloudStorage,
): Promise<boolean> {
  if (!cloud.available) return false;
  try {
    return value === null
      ? await cloud.removeItem(SKIN_CLOUD_KEY)
      : await cloud.setItem(SKIN_CLOUD_KEY, value);
  } catch {
    return false;
  }
}

/** The account-level choice, or `null` when the tier is absent, empty or unreadable. */
export async function pullSkinFromCloud(
  cloud: CloudStorageAdapter = telegram.cloudStorage,
): Promise<SkinId | null> {
  if (!cloud.available) return null;
  try {
    const raw = await cloud.getItem(SKIN_CLOUD_KEY);
    return isSkinId(raw) ? raw : null;
  } catch {
    return null;
  }
}

/* ══ applying ══════════════════════════════════════════════════════════════════════ */

/**
 * Writes {@link SKIN_ATTRIBUTE}. Exported so the picker can apply a choice on tap,
 * before any state has settled — the repaint is the feedback.
 */
export function applySkin(id: SkinId, root: HTMLElement = document.documentElement): void {
  root.setAttribute(SKIN_ATTRIBUTE, id);
}

/** `?skin=valve` in the launch URL, when it names a real skin. */
export function readSkinQuery(search?: string | null): SkinId | null {
  const raw =
    search === undefined ? (typeof window === 'undefined' ? null : window.location.search) : search;
  if (raw === null || raw === '') return null;
  try {
    const value = new URLSearchParams(raw).get(SKIN_QUERY_PARAM);
    return isSkinId(value) ? value : null;
  } catch {
    return null;
  }
}

export interface InitSkinOptions {
  readonly root?: HTMLElement;
  readonly storage?: LocalStorageLike | null;
  readonly cloud?: CloudStorageAdapter;
  /** `null` disables the query override, which is what a test wants. */
  readonly search?: string | null;
}

/**
 * Boot: apply the local answer immediately, then reconcile with the account.
 *
 * The synchronous half repeats what `index.html`'s inline script already did, because
 * this module must also be correct when that script did not run — a test, a harness,
 * a webview that stripped it. The asynchronous half is the only place the cloud tier is
 * consulted, and it repaints only when the account genuinely disagrees, so the common
 * case is one attribute write and no visible change.
 *
 * A `?skin=` in the launch URL beats both tiers for the session and is not persisted:
 * it exists to look at a skin, not to choose one.
 *
 * Returns the id that ended up applied.
 */
export async function initSkin(options: InitSkinOptions = {}): Promise<SkinId> {
  const root = options.root ?? document.documentElement;

  const forced = options.search === null ? null : readSkinQuery(options.search);
  if (forced !== null) {
    applySkin(forced, root);
    return forced;
  }

  const local = readSkin(options.storage);
  const applied = local ?? DEFAULT_SKIN;
  applySkin(applied, root);

  const remote = await pullSkinFromCloud(options.cloud ?? telegram.cloudStorage);
  if (remote === null || remote === applied) return applied;

  applySkin(remote, root);
  writeSkin(remote, options.storage);
  return remote;
}

/**
 * The whole act of choosing, in one call: apply it now, keep it locally now, and tell
 * the account about it whenever it gets round to answering.
 */
export function chooseSkin(id: SkinId, options: InitSkinOptions = {}): void {
  applySkin(id, options.root ?? document.documentElement);
  writeSkin(id, options.storage);
  void pushSkinToCloud(id, options.cloud ?? telegram.cloudStorage);
}
